import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { ApolloClient, from, InMemoryCache } from '@apollo/client';

import Config from './config';
import {
  clearAuth0AccessToken,
  clearTokenPayload,
  clearPatientTokenPayload,
  getAuth0AccessToken,
  getPatientTokenPayload,
  getTokenPayload,
} from './auth';
import { CustomGraphQLError, HomecomingError } from './errors';
import { shouldUsePatientAuth } from '../v2/lib/url';
import Auth0Helpers from './auth0';
import { customToast } from '../v2/components/ToastAlert/customToast';

const uploadHttpLink = createUploadLink({
  uri: `${Config.REACT_APP_HOMECOMING_API_BASE_URI}/graphql`,
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Don't trigger logout on network errors
  if (networkError) {
    // Background operations opt out: the toast is for work a provider just triggered, and one
    // raised by a timer they never set reads as an unexplained error in the middle of unrelated
    // work. See `suppressErrorToast` in useIntercomJwtRefresh.
    if (!operation.getContext().suppressErrorToast) {
      customToast.error('Connection error. Please try again later.');
    }
    return;
  }

  if (
    (graphQLErrors?.[0] as CustomGraphQLError)?.code ===
    HomecomingError.TokenInvalid
  ) {
    // Get the token type header from the failed operation to determine which token failed
    const tokenTypeHeader = operation.getContext().headers?.[TOKEN_TYPE_HEADER];

    // Determine which type of request this was based on the headers
    const isAuth0Request = tokenTypeHeader === AUTH0_TOKEN_TYPE;
    const usePatientAuth = shouldUsePatientAuth();

    // Only clear the specific token that failed
    if (isAuth0Request) {
      // This was an Auth0 request (provider user), clear provider tokens
      clearTokenPayload();
      clearAuth0AccessToken();
      Auth0Helpers.logout();
      window.location.href = '/login';
    } else if (usePatientAuth) {
      // This was a patient request, clear patient token only
      clearPatientTokenPayload();
      window.location.href = '/login/client';
    } else {
      // Fallback when token type is ambiguous: clear all sessions (provider app login)
      clearTokenPayload();
      clearAuth0AccessToken();
      clearPatientTokenPayload();
      Auth0Helpers.logout();
      window.location.href = '/login';
    }
  }
});

export { errorLink };

export const TOKEN_TYPE_HEADER = 'x-token-type';
export const AUTH0_TOKEN_TYPE = 'auth0';

const authLink = setContext((_, { headers }) => {
  const auth0AccessToken = getAuth0AccessToken();
  const providerTokenPayload = getTokenPayload();
  const patientTokenPayload = getPatientTokenPayload();

  const usePatientAuth = shouldUsePatientAuth();

  const isAuth0Request = !usePatientAuth && !!auth0AccessToken;

  const authToken = usePatientAuth
    ? patientTokenPayload?.authSessionToken
    : auth0AccessToken || providerTokenPayload?.authToken;

  return {
    headers: {
      ...headers,
      ...(authToken && {
        authorization: `Bearer ${authToken}`,
      }),
      // This header is used to differentiate between Auth0 and other token types.
      // Critically, this differentiates between the server running our existing authentication
      // scheme for clients/patients, as well as both providers & clients/patients on the mobile app.
      ...(isAuth0Request && {
        [TOKEN_TYPE_HEADER]: AUTH0_TOKEN_TYPE,
      }),
    },
  };
});

const retryLink = new RetryLink({
  attempts: {
    // GQL errors don't trigger retries, only network errors (according to the docs).
    retryIf: (error) => {
      if (typeof error !== 'object') {
        return false;
      }

      // Internet is offline or server is down. Match Chrome's "Failed to fetch"
      // Firefox's "NetworkError when attempting to fetch resource." (WEB-9M),
      // and Safari's "Load failed" (WEB-H).
      const msg = error?.message;
      return (
        msg === 'Failed to fetch' ||
        msg === 'NetworkError when attempting to fetch resource.' ||
        msg === 'Load failed'
      );
    },
  },
});

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, retryLink, uploadHttpLink]),
  cache: new InMemoryCache({
    possibleTypes: {
      Activity: [
        'ActivityAssessment',
        'ActivityAction',
        'ActivityAudio',
        'ActivityImage',
        'ActivityLink',
        'ActivityPDF',
        'ActivityText',
      ],
    },
    // Cache policies defined by type, e.g. how to merge incoming data with existing data.
    typePolicies: {
      ProgramTemplateTaskGroup: {
        fields: {
          tasks: {
            merge(existing, incoming) {
              return incoming;
            },
          },
        },
      },
      Task: {
        fields: {
          media: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
    },
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'no-cache',
    },
  },
});
